import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable } from 'rxjs';
import { Patient } from 'src/app/models/patient.model';
import { User } from 'src/app/models/user.model';
import { environment } from 'src/environments/environment';
import { CommunicationsRequest, CommunicationsResponse } from '../communications/models/communication.model';
import {
  Attachments,
  PatientCommunicationRecord,
  PatientCommunicationType,
} from '../models/patient-communication.model';

@Injectable({
  providedIn: 'root',
})
export class PatientCommunicationService {
  baseUrl = `${environment.welbyEndpoint}/api/v1/core/patients`;
  constructor(private db: AngularFirestore, private http: HttpClient) {}

  buildCommunicationRecord(
    subject: string,
    message: string,
    patient: Patient,
    currentUser: User,
    type: PatientCommunicationType,
    recipients?: string[],
    attachments?: Attachments[],
    isIdentityVerified?: boolean,
    isRecordConsent?: boolean
  ): PatientCommunicationRecord {
    const communicationRecord: PatientCommunicationRecord = {
      type,
      note: { subject, message },
      createdAt: new Date(),
      createdBy: { name: `${currentUser?.firstName} ${currentUser?.lastName}`, userId: currentUser?.user_id },
      patient: {
        patientId: patient?.user_id ?? '',
        name: `${patient?.firstName ?? 'N/A'} ${patient?.lastName ?? 'N/A'}`,
      },
      ...(type === PatientCommunicationType.CALL && isIdentityVerified !== undefined && { isIdentityVerified }),
      ...(type === PatientCommunicationType.CALL && isRecordConsent !== undefined && { isRecordConsent }),
    };
    if (attachments) {
      communicationRecord['attachments'] = attachments.map((attachment) => ({
        fileName: attachment.fileName,
        contentType: attachment.contentType,
      }));
    }
    if (recipients && recipients.length > 0) {
      communicationRecord['fromDashboard'] = { recipients };
    }
    return communicationRecord;
  }

  getIcon(type: PatientCommunicationType): string {
    const icons = {
      [PatientCommunicationType.EMAIL]: 'mail',
      [PatientCommunicationType.SMS]: 'sms',
      [PatientCommunicationType.CALL]: 'call',
      [PatientCommunicationType.VIDEO_CONFERENCE]: 'duo',
    };
    return icons[type];
  }

  saveCommunicationRecord(record: any, patientId: string): Promise<any> {
    try {
      const communicationRef = this.db.collection('users').doc(patientId).collection('communications');
      return communicationRef.add(record);
    } catch (error) {
      throw error;
    }
  }

  saveCommunicationRecordId(record: any, patientId: string, messageId: string): Promise<any> {
    try {
      const communicationRef = this.db.collection('users').doc(patientId).collection('communications').doc(messageId);
      return communicationRef.set(record);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retrieves all communications for a specific patient with pagination support.
   *
   * @param patientId - The unique identifier of the patient
   * @param paginatorParams - Optional pagination and sorting parameters including page, pageSize, sortBy, and sortDirection
   * @returns An Observable that resolves to CommunicationsResponse containing the paginated list of communications and metadata
   */
  getCommunicationsByPatient(
    patientId: string,
    paginatorParams?: CommunicationsRequest
  ): Observable<CommunicationsResponse> {
    const url = `${this.baseUrl}/${patientId}/communications/search`;
    return this.http.post<CommunicationsResponse>(url, paginatorParams);
  }
}
